import React from "react";
import { FormikValues } from "formik";
import { object, string, array } from "yup";
import { ModalV2 } from "@jsq/ds/core";
import { Form, SubmitButton, CancelButton } from "@jsq/ds/forms";
import { CustomFieldType, PrefillOption, CustomFieldOption, Section } from "../../types";
import * as styles from "../styles_vanilla.css";
import ModalBodyContent from "./ModalBodyContent";

interface Props<T extends FormikValues = FormikValues> {
  show: boolean;
  onHide: () => void;
  title: string;
  submitButtonText: string;
  submitButtonTestId: string;
  isSubmitting: boolean;
  initialValues: T;
  onSubmit: (values: Partial<T>) => Promise<void>;
  isEditMode?: boolean;
  sections?: Section[] | null;
}

const validationSchema = object().shape({
  name: string()
    .required("Field name is required")
    .max(44, "Field name cannot exceed 44 characters"),
  type: string().required("Field type is required"),
  options: array().when("type", {
    is: (type: string) => type === CustomFieldType.ENUMERATION,
    then: (schema) =>
      schema
        .min(1, "At least one option is required for enumeration fields")
        .test(
          "no-empty-labels",
          "All enumeration options must have non-empty labels",
          function (options) {
            if (!Array.isArray(options)) return true;

            // Check if any option has an empty or whitespace-only label
            const hasEmptyLabels = options.some(
              (option: CustomFieldOption) =>
                !option?.label ||
                typeof option.label !== "string" ||
                option.label.trim() === "",
            );

            if (hasEmptyLabels) {
              // Create a validation error that can be used to highlight specific fields
              return this.createError({
                message: "All enumeration options must have non-empty labels",
                path: this.path,
              });
            }

            return true;
          },
        )
        .test(
          "no-duplicate-labels",
          "Enumeration options must have unique labels",
          function (options) {
            if (!Array.isArray(options)) return true;

            // Extract and normalize labels for comparison
            const labels = options
              .map((option: CustomFieldOption) => option?.label?.trim()?.toLowerCase())
              .filter((label: string) => label && label.length > 0);

            // Check for duplicates using Set
            const uniqueLabels = new Set(labels);

            if (labels.length !== uniqueLabels.size) {
              // Find the first duplicate for a more specific error message
              const labelCounts = new Map<string, number>();
              let duplicateLabel = "";

              for (const label of labels) {
                const count = (labelCounts.get(label) || 0) + 1;
                labelCounts.set(label, count);

                if (count > 1 && !duplicateLabel) {
                  duplicateLabel = label;
                }
              }

              return this.createError({
                message: duplicateLabel
                  ? `Duplicate label found: "${duplicateLabel}". Each option must have a unique label.`
                  : "Enumeration options must have unique labels",
                path: this.path,
              });
            }

            return true;
          },
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  prefillOption: string().oneOf(Object.values(PrefillOption)),
});

const CustomFieldModal = <T extends FormikValues = FormikValues>({
  show,
  onHide,
  title,
  submitButtonText,
  submitButtonTestId,
  isSubmitting,
  initialValues,
  onSubmit,
  isEditMode = false,
  sections = null,
}: Props<T>) => {
  return (
    <ModalV2 show={show} onHide={onHide} title={title}>
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <ModalV2.Body>
          <ModalBodyContent isEditMode={isEditMode} sections={sections} />
        </ModalV2.Body>

        <ModalV2.Footer>
          <div className={styles.modalFooter}>
            <CancelButton onClick={onHide} disabled={isSubmitting}>
              Cancel
            </CancelButton>
            <SubmitButton isLoading={isSubmitting} data-testid={submitButtonTestId}>
              {submitButtonText}
            </SubmitButton>
          </div>
        </ModalV2.Footer>
      </Form>
    </ModalV2>
  );
};

export default CustomFieldModal;
